import React from 'react';
import { Box, Typography, Button, Grid, Card, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import { profile } from '../../data/profile';
import { nowData } from '../../data/now';
import { projects } from '../../data/projects';
import { bookDetails, writingItems } from '../../data/writing';
import { learningFocus } from '../../data/learning';
import SectionLabel from '../primitives/SectionLabel';
import TechnicalMeta from '../primitives/TechnicalMeta';
import EditorialRow from '../primitives/EditorialRow';
import DividerRule from '../primitives/DividerRule';
import HeroTelemetryHUD from './HeroTelemetryHUD';
import PhilosophyTicker from './PhilosophyTicker';
import PortalTeleportMatrix from './PortalTeleportMatrix';
import { playClickSound } from '../../utils/soundEffects';

const Home = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Building':
        return { color: isDark ? '#38bdf8' : '#0284c7', bg: isDark ? 'rgba(56, 189, 248, 0.06)' : 'rgba(2, 132, 199, 0.05)', border: isDark ? 'rgba(56, 189, 248, 0.28)' : 'rgba(2, 132, 199, 0.22)' };
      case 'Writing':
        return { color: isDark ? '#c084fc' : '#7c3aed', bg: isDark ? 'rgba(192, 132, 252, 0.06)' : 'rgba(124, 58, 237, 0.05)', border: isDark ? 'rgba(192, 132, 252, 0.28)' : 'rgba(124, 58, 237, 0.22)' };
      case 'Studying':
        return { color: isDark ? '#34d399' : '#059669', bg: isDark ? 'rgba(52, 211, 153, 0.06)' : 'rgba(5, 150, 105, 0.05)', border: isDark ? 'rgba(52, 211, 153, 0.28)' : 'rgba(5, 150, 105, 0.22)' };
      case 'Reading':
        return { color: isDark ? '#fbbf24' : '#d97706', bg: isDark ? 'rgba(251, 191, 36, 0.06)' : 'rgba(217, 119, 6, 0.05)', border: isDark ? 'rgba(251, 191, 36, 0.28)' : 'rgba(217, 119, 6, 0.22)' };
      case 'Exploring':
      default:
        return { color: isDark ? '#fb7185' : '#e11d48', bg: isDark ? 'rgba(251, 113, 133, 0.06)' : 'rgba(225, 29, 72, 0.05)', border: isDark ? 'rgba(251, 113, 133, 0.28)' : 'rgba(225, 29, 72, 0.22)' };
    }
  };

  const getNowIcon = (category) => {
    const c = getCategoryColor(category).color;
    switch (category) {
      case 'Building':
        return <CodeOutlinedIcon sx={{ color: c, fontSize: 24 }} />;
      case 'Writing':
        return <HistoryEduOutlinedIcon sx={{ color: c, fontSize: 24 }} />;
      case 'Studying':
        return <SchoolOutlinedIcon sx={{ color: c, fontSize: 24 }} />;
      case 'Reading':
        return <MenuBookOutlinedIcon sx={{ color: c, fontSize: 24 }} />;
      case 'Exploring':
      default:
        return <ExploreOutlinedIcon sx={{ color: c, fontSize: 24 }} />;
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* =========================================================================
          HERO SECTION — PERSONAL IDENTITY & ESSENCE
          ========================================================================= */}
      <Box sx={{ py: { xs: 4, md: 7 }, mb: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <Grid container spacing={{ xs: 4, md: 4, lg: 6 }} alignItems="center">
            <Grid item xs={12} md={7} lg={7}>
              {/* Archival Coordinate Badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(5, 150, 105, 0.1)',
                    px: 1.6,
                    py: 0.45,
                    borderRadius: '9999px',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(52, 211, 153, 0.35)' : '1px solid rgba(5, 150, 105, 0.35)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 0 16px rgba(16, 185, 129, 0.15)' : '0 0 12px rgba(5, 150, 105, 0.08)',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      boxShadow: '0 0 8px #10b981',
                      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                    }}
                  />
                  <Typography
                    variant="overline"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#34d399' : '#059669',
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      fontSize: '0.74rem',
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    {'PERSONAL ARCHIVE // INDIA · 2026'}
                  </Typography>
                </Box>
              </Box>

              {/* Multi-Dimensional Roles */}
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  color: theme.palette.secondary.main,
                  textTransform: 'uppercase',
                  mb: 1.5,
                }}
              >
                BUILDER · PROGRAMMER · WRITER · READER · STUDENT OF SYSTEMS
              </Typography>

              {/* Name Display Title */}
              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                  fontSize: { xs: '2.8rem', sm: '3.8rem', md: '4.6rem' },
                  fontWeight: 600,
                  lineHeight: 1.08,
                  mb: 2.5,
                  letterSpacing: '-0.03em',
                  color: theme.palette.text.primary,
                }}
              >
                {profile.name}
              </Typography>

              {/* Core Identity Statement */}
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                  color: theme.palette.text.primary,
                  maxWidth: 680,
                  mb: 2,
                  fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.55rem' },
                  lineHeight: 1.45,
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                "{profile.headline}"
              </Typography>

              {/* Supporting Summary */}
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 640,
                  mb: 4,
                  fontSize: { xs: '0.98rem', md: '1.05rem' },
                  lineHeight: 1.75,
                }}
              >
                {profile.introduction[0]}
              </Typography>

              {/* Focused Hero CTAs */}
              <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  component={Link}
                  to="/work"
                  onClick={playClickSound}
                  variant="contained"
                  color="primary"
                  size="medium"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{ py: 1, px: 2.5, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Explore Selected Work
                </Button>
                <Button
                  component={Link}
                  to="/writing"
                  onClick={playClickSound}
                  variant="outlined"
                  size="medium"
                  sx={{ py: 1, px: 2.5, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  Writing & Book
                </Button>
                <Button
                  onClick={() => {
                    playClickSound();
                    window.dispatchEvent(new CustomEvent('open-recruiter-modal'));
                  }}
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  startIcon={<FlashOnIcon sx={{ fontSize: 17 }} />}
                  sx={{
                    py: 1,
                    px: 2.2,
                    borderRadius: '10px',
                    fontWeight: 700,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.82rem',
                    textTransform: 'none'
                  }}
                >
                  Recruiter 30s Briefing
                </Button>
                <Button
                  component={Link}
                  to="/about"
                  onClick={playClickSound}
                  variant="text"
                  size="medium"
                  sx={{ py: 1, px: 2, borderRadius: '10px', color: theme.palette.text.secondary, textTransform: 'none', fontWeight: 600 }}
                >
                  About the Person →
                </Button>
              </Box>
            </Grid>

            {/* Profile Photo — Circular, Enlarged, Luxury Glow */}
            <Grid item xs={12} md={5} lg={5} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: 'center', mt: { xs: 2, md: 0 } }}>
              <Box
                component="img"
                src={profile.profileImage || "/profile.jpg"}
                alt={profile.name}
                sx={{
                  width: { xs: 220, sm: 280, md: 360, lg: 420 },
                  height: { xs: 220, sm: 280, md: 360, lg: 420 },
                  aspectRatio: '1 / 1',
                  maxWidth: '100%',
                  display: 'block',
                  objectFit: 'cover',
                  objectPosition: 'center 15%',
                  borderRadius: '50%',
                  border: theme.palette.mode === 'dark'
                    ? '3.5px solid rgba(229, 169, 60, 0.5)'
                    : '3.5px solid rgba(184, 134, 11, 0.4)',
                  outline: theme.palette.mode === 'dark'
                    ? '6px solid rgba(229, 169, 60, 0.14)'
                    : '6px solid rgba(184, 134, 11, 0.1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 24px 64px rgba(0, 0, 0, 0.75), 0 0 45px rgba(229, 169, 60, 0.22)'
                    : '0 20px 48px rgba(0, 0, 0, 0.14), 0 0 32px rgba(184, 134, 11, 0.15)',
                  filter: theme.palette.mode === 'dark' 
                    ? 'contrast(1.04) brightness(0.98)' 
                    : 'contrast(1.01)',
                  transition: 'transform 350ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 350ms cubic-bezier(0.16, 1, 0.3, 1)',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 30px 80px rgba(0, 0, 0, 0.85), 0 0 60px rgba(229, 169, 60, 0.35)'
                      : '0 24px 56px rgba(0, 0, 0, 0.18), 0 0 42px rgba(184, 134, 11, 0.25)',
                  }
                }}
              />
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* =========================================================================
          HERO LIVE TELEMETRY HUD (4 TELEMETRY CARDS + RECRUITER CTA)
          ========================================================================= */}
      <HeroTelemetryHUD />

      {/* =========================================================================
          SECTION 01: "NOW" — WHAT AM I DOING NOW? (5 ACTIVE PILLARS)
          ========================================================================= */}
      <Box sx={{ py: 3 }}>
        <SectionLabel index="01" title="Currently" meta={`Active State // ${nowData.lastUpdated}`} />
        
        <Box sx={{ mb: 3.5, maxWidth: 780 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontSize: { xs: '1.7rem', md: '2.3rem' }, mb: 1, fontWeight: 600 }}>
            {nowData.headline}
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            {nowData.subheadline}
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {nowData.items.map((item) => {
            const catStyle = getCategoryColor(item.category);
            return (
              <Grid item xs={12} sm={6} md={item.id === 'building' || item.id === 'writing' ? 6 : 4} key={item.id}>
                <Card
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    backgroundColor: catStyle.bg,
                    border: `1px solid ${catStyle.border}`,
                    borderRadius: '16px',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: catStyle.color,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 12px 28px -8px ${catStyle.color}22`,
                    },
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getNowIcon(item.category)}
                        <Typography
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.74rem',
                            fontWeight: 800,
                            color: catStyle.color,
                            letterSpacing: '0.08em',
                          }}
                        >
                          {item.category.toUpperCase()}
                        </Typography>
                      </Box>
                      <TechnicalMeta label={item.status} />
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        mb: 1.2,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: isDark ? '#cbd5e1' : '#475569',
                        lineHeight: 1.68,
                        fontSize: '0.88rem',
                        mb: 2.5,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>

                  <Box sx={{ pt: 1.5, borderTop: `1px solid ${catStyle.border}` }}>
                    <Button
                      component={Link}
                      to={item.route}
                      onClick={playClickSound}
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 13 }} />}
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        px: 0,
                        color: catStyle.color,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {item.actionLabel}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* =========================================================================
          PORTAL TELEPORT MATRIX (BENTO-GRID OF LUXURY DESTINATIONS)
          ========================================================================= */}
      <PortalTeleportMatrix />

      {/* =========================================================================
          SYSTEMS & STATECRAFT INVARIANT PHILOSOPHY TICKER
          ========================================================================= */}
      <PhilosophyTicker />

      {/* =========================================================================
          SECTION 02: WHAT DO I BUILD? (SELECTED WORK)
          ========================================================================= */}
      <Box sx={{ py: 3 }}>
        <SectionLabel index="02" title="Selected Work" meta={`Engineering // ${projects.length} Systems`} />
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h2" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontSize: { xs: '1.6rem', md: '2.1rem' }, fontWeight: 600 }}>
            Software Systems & Implementations
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {projects.slice(0, 3).map((proj, idx) => (
            <EditorialRow
              key={proj.id}
              index={`0${idx + 1}`}
              title={proj.title}
              subtitle={proj.shortDescription}
              meta={`${proj.category} · ${proj.year}`}
              to={`/work/${proj.slug}`}
            />
          ))}
        </Box>

        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            component={Link}
            to="/work"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: '8px' }}
          >
            View all engineering case studies ({projects.length})
          </Button>
        </Box>
      </Box>

      {/* =========================================================================
          SECTION 03: WHAT DO I WRITE? (BOOK & ESSAYS)
          ========================================================================= */}
      <Box sx={{ py: 4 }}>
        <SectionLabel index="03" title="Writing & Inquiry" meta="Manuscript & Essays" />

        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Book Manuscript Highlight */}
          <Grid item xs={12} md={7}>
            <Card
              className="codex-card"
              sx={{
                p: { xs: 3, md: 3.5 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
                  <AutoStoriesOutlinedIcon color="secondary" sx={{ fontSize: 22 }} />
                  <TechnicalMeta label="BOOK // IN PROGRESS" />
                </Box>
                <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 0.5, fontSize: '1.45rem' }}>
                  {bookDetails.title}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: theme.palette.secondary.main, mb: 2, fontStyle: 'italic', fontSize: '0.94rem' }}>
                  {bookDetails.subtitle}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2.5, lineHeight: 1.7 }}>
                  {bookDetails.description}
                </Typography>
              </Box>

              <Button
                component={Link}
                to="/writing/book"
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{ alignSelf: 'flex-start', borderRadius: '8px' }}
              >
                Read Book Overview & Themes
              </Button>
            </Card>
          </Grid>

          {/* Recent Essay Spotlight */}
          <Grid item xs={12} md={5}>
            <Card
              className="codex-card"
              sx={{
                p: { xs: 3, md: 3.5 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <HistoryEduOutlinedIcon color="secondary" sx={{ fontSize: 22 }} />
                  <TechnicalMeta label="FEATURED ESSAY" />
                </Box>
                <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 1, fontSize: '1.35rem' }}>
                  {writingItems[0].title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2.5, lineHeight: 1.68 }}>
                  {writingItems[0].summary}
                </Typography>
              </Box>

              <Button
                component={Link}
                to={`/writing/${writingItems[0].slug}`}
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{ alignSelf: 'flex-start', borderRadius: '8px', color: theme.palette.secondary.main }}
              >
                Read Essay
              </Button>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            component={Link}
            to="/writing"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: '8px' }}
          >
            Explore all essays & notes ({writingItems.length})
          </Button>
        </Box>
      </Box>

      {/* =========================================================================
          SECTION 04: WHAT DO I READ & RETURN TO? (LIBRARY & HISTORY)
          ========================================================================= */}
      <Box sx={{ py: 4 }}>
        <SectionLabel index="04" title="Reading & Inquiry Space" meta="Library & Statecraft" />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              component={Link}
              to="/reading/bookshelf"
              className="codex-card"
              sx={{
                p: 3.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                textDecoration: 'none',
              }}
            >
              <Box>
                <MenuBookOutlinedIcon color="secondary" sx={{ fontSize: 32, mb: 1.5 }} />
                <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 1, fontSize: '1.35rem', color: theme.palette.text.primary }}>
                  Digital Bookshelf & Personal Library
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2.5, lineHeight: 1.7 }}>
                  A curated archive of foundational works across history, statecraft, literature, philosophy, and computer science. Searchable and categorized.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: theme.palette.secondary.main, fontSize: '0.86rem', fontWeight: 600 }}>
                <span>Browse Digital Bookshelf</span>
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              component={Link}
              to="/history"
              className="codex-card"
              sx={{
                p: 3.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                textDecoration: 'none',
              }}
            >
              <Box>
                <HistoryEduOutlinedIcon color="secondary" sx={{ fontSize: 32, mb: 1.5 }} />
                <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 1, fontSize: '1.35rem', color: theme.palette.text.primary }}>
                  History & Civilizational Trajectories
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2.5, lineHeight: 1.7 }}>
                  Inquiry into historical statecraft, institutional incentives, civilizational trajectories, and classical treatises on governance and human systems.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: theme.palette.secondary.main, fontSize: '0.86rem', fontWeight: 600 }}>
                <span>Explore History Timeline</span>
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* =========================================================================
          SECTION 05: WHAT AM I TRYING TO UNDERSTAND? (LEARNING & KNOWLEDGE)
          ========================================================================= */}
      <Box sx={{ py: 4 }}>
        <SectionLabel index="05" title="Structured Study & Knowledge" meta="Disciplines" />

        <Card className="codex-card" sx={{ p: { xs: 3.5, md: 4.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <SchoolOutlinedIcon color="secondary" sx={{ fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, fontSize: '1.45rem' }}>
              Interdisciplinary Study: Read → Learn → Think → Write → Build
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3.5, maxWidth: 800, lineHeight: 1.75 }}>
            Structured exploration across constitutional polity, public administration, macro-history, political economy (UPSC / SSC curriculum foundations), and algorithmic problem solving on Codeforces.
          </Typography>

          <Grid container spacing={2}>
            {learningFocus.slice(0, 4).map((f) => (
              <Grid item xs={12} sm={6} md={3} key={f.id}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.92rem' }}>
                    {f.discipline}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    {f.status}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              component={Link}
              to="/learning"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: '8px' }}
            >
              Explore Knowledge Map & Methodology
            </Button>
          </Box>
        </Card>
      </Box>

      {/* =========================================================================
          SECTION 06: HOW DID I GET HERE? (THE JOURNEY GLIMPSE)
          ========================================================================= */}
      <Box sx={{ py: 4 }}>
        <SectionLabel index="06" title="Personal Evolution" meta="Chronology" />

        <Card className="codex-card" sx={{ p: { xs: 3.5, md: 4.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <TimelineOutlinedIcon color="secondary" sx={{ fontSize: 26 }} />
            <Typography variant="h5" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, fontSize: '1.45rem' }}>
              The Path: Education → Engineering → Independent Building → Present
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3.5, maxWidth: 800, lineHeight: 1.75 }}>
            Graduated with a B.Tech in CSE in 2022, worked on frontend client panels at Knoldus, achieved Candidate Master on Codeforces (2013 max rating), and dedicated time after graduation to independent software systems building, technical book authoring, and deep study.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              component={Link}
              to="/journey"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: '8px' }}
            >
              View Full Journey Timeline
            </Button>
          </Box>
        </Card>
      </Box>

      {/* =========================================================================
          SECTION 07: CURATED VISITOR PATHWAYS (WHERE CAN YOU GO NEXT?)
          ========================================================================= */}
      <Box sx={{ py: 4 }}>
        <SectionLabel index="07" title="Curated Pathways" meta="Directory" />

        <Grid container spacing={3}>
          {/* Pathway 1: For Technical Recruiters & Engineers */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              component={Link}
              to="/work"
              className="codex-card"
              sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textDecoration: 'none' }}
            >
              <Box>
                <CodeOutlinedIcon color="secondary" sx={{ fontSize: 26, mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
                  Technical Engineering
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Case studies in Unix CLI tools, C POSIX shell, LaTeX DSA reference book, and systems.
                </Typography>
              </Box>
              <Box sx={{ pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.secondary.main }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>FOR RECRUITERS</Typography>
                <ArrowForwardIcon sx={{ fontSize: 13 }} />
              </Box>
            </Card>
          </Grid>

          {/* Pathway 2: For Readers & Literary Thinkers */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              component={Link}
              to="/reading"
              className="codex-card"
              sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textDecoration: 'none' }}
            >
              <Box>
                <MenuBookOutlinedIcon color="secondary" sx={{ fontSize: 26, mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
                  Reading & Library
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Curated bookshelf across computing, philosophy, novels, and statecraft.
                </Typography>
              </Box>
              <Box sx={{ pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.secondary.main }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>FOR READERS</Typography>
                <ArrowForwardIcon sx={{ fontSize: 13 }} />
              </Box>
            </Card>
          </Grid>

          {/* Pathway 3: For Collaborators & Builders */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              component={Link}
              to="/work/building"
              className="codex-card"
              sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textDecoration: 'none' }}
            >
              <Box>
                <PsychologyOutlinedIcon color="secondary" sx={{ fontSize: 26, mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
                  Currently Building
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Independent software products and developer tooling in active stealth prototyping.
                </Typography>
              </Box>
              <Box sx={{ pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.secondary.main }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>FOR BUILDERS</Typography>
                <ArrowForwardIcon sx={{ fontSize: 13 }} />
              </Box>
            </Card>
          </Grid>

          {/* Pathway 4: For Curious Explorers */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              component={Link}
              to="/about"
              className="codex-card"
              sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textDecoration: 'none' }}
            >
              <Box>
                <ExploreOutlinedIcon color="secondary" sx={{ fontSize: 26, mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
                  About the Person
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Core philosophy, background, intellectual domains, and direct contact channels.
                </Typography>
              </Box>
              <Box sx={{ pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.secondary.main }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>FOR EXPLORERS</Typography>
                <ArrowForwardIcon sx={{ fontSize: 13 }} />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* =========================================================================
          CLOSING COLOPHON / CONTACT INVITATION
          ========================================================================= */}
      <DividerRule sx={{ my: 6 }} />

      <Box
        sx={{
          py: 4,
          textAlign: 'center',
          maxWidth: 650,
          mx: 'auto',
        }}
      >
        <Typography variant="overline" sx={{ color: theme.palette.secondary.main, fontWeight: 700, letterSpacing: '0.12em', mb: 1, display: 'block' }}>
          CORRESPONDENCE
        </Typography>
        <Typography variant="h3" sx={{ fontFamily: '"Newsreader", "Playfair Display", Georgia, serif', fontSize: { xs: '1.6rem', md: '2.1rem' }, mb: 1.5, fontWeight: 600, color: theme.palette.text.primary }}>
          Open for Inquiries & Dialogue
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3, lineHeight: 1.72, fontSize: '0.98rem' }}>
          Feel free to reach out for conversations on software architecture, writing, history, or engineering collaborations.
        </Typography>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{ py: 1.1, px: 3.2, borderRadius: '10px' }}
        >
          Get in Touch
        </Button>
      </Box>
    </Box>
  );
};

export default Home;
